import React from "react";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardProps,
  Center,
  Divider,
  Heading,
  Text,
  List,
  ListItem,
} from "@chakra-ui/react";
import { DocumentData, WithFieldValue } from "firebase/firestore";

import { AuthUser } from "@constants";
import { useAuth } from "@hooks";
import { isUserAdmin, userMatchesUID } from "@utils";
import { EditUserModal } from "./EditUserModal";

interface IUserDetails extends CardProps {
  userId: string;
  userDetails: AuthUser;
  updateUser: (data: WithFieldValue<DocumentData>) => Promise<void>;
}

function UserDetails({
  userId,
  userDetails,
  updateUser,
  ...delegated
}: IUserDetails) {
  const { user: viewer } = useAuth();

  const isViewerAdmin = React.useMemo<boolean>(
    () => isUserAdmin(viewer),
    [viewer],
  );

  const displayHiddenInfo = React.useMemo<boolean>(
    () => isViewerAdmin && userMatchesUID(viewer, userId),
    [isViewerAdmin, viewer, userId],
  );

  return (
    <Card {...delegated}>
      <CardHeader>
        <Heading as="h2">
          {userDetails.firstName} {userDetails.lastName}
        </Heading>
      </CardHeader>
      <Center>
        <Divider width="95%" />
      </Center>
      <CardBody as={List}>
        <ListItem>
          <Text as="strong">User ID: </Text>
          {userId}
        </ListItem>
        {displayHiddenInfo ? (
          <ListItem>
            <Text as="strong">Email: </Text>
            {userDetails.email}
          </ListItem>
        ) : undefined}
        {displayHiddenInfo ? (
          <ListItem>
            <Text as="strong">Location: </Text>
            {userDetails.location}
          </ListItem>
        ) : undefined}
        <ListItem>
          <Text as="strong">Role:</Text>
          {userDetails.role}
        </ListItem>
      </CardBody>
      {displayHiddenInfo ? (
        <>
          <Center>
            <Divider width="95%" />
          </Center>
          <CardFooter>
            <EditUserModal
              isViewerAdmin={displayHiddenInfo}
              user={userDetails}
              updateUser={updateUser}
            />
          </CardFooter>
        </>
      ) : undefined}
    </Card>
  );
}

export { UserDetails };
